import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  Breadcrumbs,
  Button,
  CardComponent,
  Chat,
  Columns,
  DashedStandardDropdown,
  NoDataVisual,
  PageTitleAndSubTitle,
  Tooltip,
  WasteCalculatorCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { ChangeEvent, useEffect, useState } from 'react';
import Loader from '../../_components/molecules/Loader';
import { pageScrollDown } from '../SummaryPage';
import TabSelection from '../../_components/molecules/TabSelection';
import { observer } from 'mobx-react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import axios from 'axios';
import properties from '../../core-utils/properties';
import {
  createChatMessage,
  getChatHistory,
  ChatMessage,
  initialiseChatWithFile,
} from '../../core-utils/services/chatService';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import ContractReport from '../../../public/Images/AccessDashboard/ContractReport.png';
import { useStores } from '../../stores';
import {
  INTER_CONTRACT_REPORT,
  PLAN_DOCUMENTS,
  REGULATORY_COMPLIANCE_REPORT,
} from '../../core-utils/routes';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  createBlobUrl,
  formatMatrixData,
  formatNewProvisions,
  getCategoryByFeature,
  getReportStatusText,
  MatrixData,
} from '../../core-utils/helper-utils/contractAIQHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { cellStyles1 } from '../../_components/molecules/PatientDetailsMedicalHistoryTab';
import {
  formatMemberClaim,
  getTextWithToolTip,
} from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import MedicalHistoryDetails from '../../_components/organisms/MedicalHistoryDetails';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  arrayToString,
  commasForNumbers,
} from '../../core-utils/helper-utils/helper';
import {
  dropDownMenuItemsAlphabets,
  dropDownMenuItemsValues,
  handleHeaderMouseEnter,
  handleHeaderMouseLeave,
} from '../../core-utils/helper-utils/tableSortHelper';
import moment from 'moment';
import { UserAccessConstants } from '../../core-utils/helper-utils/userAccess';
import { extractPlanDocsReport } from '../../core-utils/helper-utils/contractReportHelper';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import CoverageCheck from '../../_components/organisms/CoverageCheck';
import SummaryOfBenefitsConstants from '../../core-utils/messages/SummaryOfBenefitsConstants';
import ReactMarkdown from 'react-markdown';
import ProvisionMatrix from '../../_components/organisms/ProvisionMatrix';
import { Columns as MatrixColumns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/MatrixTable';
import SvgInfo from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import { getBooleanCell } from '../ContractDetailsPage';
import { DottedText } from '../../core-utils/helper-utils/financialForensicsHelper';
import { DocumentDetailsDataType } from '../../core-utils/helper-utils/termCompilerHelper';
import PdfHighlighter from '../../_components/organisms/PdfHighlighter';
import { extractAndSaveProvisionMatrixData } from '../../core-utils/helper-utils/contractMetadataHelper';
import { DynamicFilter } from '../../_components/molecules/DynamicFilter';
import { PDF_WORKER_URL } from '../../core-utils/helper-utils/pdfWorker';

export interface PlanDocumentDetailsPageProps {}

export interface Document {
  document_name: string;
  document_type: string;
  document_url: string;
}

export interface PlanDocuments {
  _id: string;
  plan_type: string;
  plan_end_date: string;
  plan_start_date: string;
  documents: Document[];
  reports: string[];
}

const useStyles = makeStyles()({
  breadcrumbs: {
    padding: '12px 20px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  root: {
    padding: '24px 64px',
  },
  reports: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  eachReport: {
    margin: '0px 32px 32px 0px',
  },
  tabs: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  tabContent: {
    overflow: 'auto',
    paddingTop: '32px',
    height: '70vh',
    ...scrollBarStyles,
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  planAndDocGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: '60px',
  },
  documentViewContainer: {
    display: 'flex',
    gap: '24px',
    position: 'relative',
  },
  documentSection: {
    flex: '0 0 70%',
  },
  chatSection: {
    flex: '0 0 calc(30% - 24px)',
    position: 'sticky',
    top: '24px',
    alignSelf: 'flex-start',
    height: '100vh',
    '& > *': {
      height: '100vh',
    },
    paddingRight: '16px',
  },
  iconWrapper: {
    width: '16px',
    height: '16px',
    marginLeft: '4px',
  },
  provisionGrid: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '300px',
  },
  parameterBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rightView: {
    width: '45%',
  },
});

// Markdown Message Component for AI responses
const MarkdownMessage: React.FC<{ content: string }> = ({ content }) => {
  return (
    <ReactMarkdown
      components={{
        // Customize rendering for different markdown elements

        p: ({ node, ...props }: any) => (
          <Typography
            fontSize="14px"
            lineHeight="21px"
            fontType="regular"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="0 0 8px 0"
            {...props}
          />
        ),

        h1: ({ node, ...props }: any) => (
          <Typography
            fontSize="20px"
            lineHeight="28px"
            fontType="bold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="16px 0 8px 0"
            {...props}
          />
        ),

        h2: ({ node, ...props }: any) => (
          <Typography
            fontSize="18px"
            lineHeight="26px"
            fontType="bold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="14px 0 8px 0"
            {...props}
          />
        ),

        h3: ({ node, ...props }: any) => (
          <Typography
            fontSize="16px"
            lineHeight="24px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin="12px 0 8px 0"
            {...props}
          />
        ),

        code: ({ node, inline, ...props }: any) =>
          inline ? (
            <code
              style={{
                backgroundColor: patientAccessTheme.palette.grey[100],
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'monospace',
              }}
              {...props}
            />
          ) : (
            <pre
              style={{
                backgroundColor: patientAccessTheme.palette.grey[100],
                padding: '12px',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '13px',
                fontFamily: 'monospace',
                margin: '8px 0',
              }}
            >
              <code {...props} />
            </pre>
          ),

        ul: ({ node, ...props }: any) => (
          <ul
            style={{
              margin: '8px 0',
              paddingLeft: '24px',
            }}
            {...props}
          />
        ),

        ol: ({ node, ...props }: any) => (
          <ol
            style={{
              margin: '8px 0',
              paddingLeft: '24px',
            }}
            {...props}
          />
        ),

        li: ({ node, ...props }: any) => (
          <li
            style={{
              marginBottom: '4px',
              fontSize: '14px',
              lineHeight: '21px',
              color: patientAccessTheme.palette.text.highemphasis,
            }}
            {...props}
          />
        ),

        strong: ({ node, ...props }: any) => (
          <strong
            style={{
              fontWeight: 600,
              color: patientAccessTheme.palette.text.highemphasis,
            }}
            {...props}
          />
        ),

        a: ({ node, ...props }: any) => (
          <a
            style={{
              color: patientAccessTheme.palette.primary[500],
              textDecoration: 'none',
            }}
            {...props}
          />
        ),

        blockquote: ({ node, ...props }: any) => (
          <blockquote
            style={{
              borderLeft: `4px solid ${patientAccessTheme.palette.primary[500]}`,
              paddingLeft: '16px',
              margin: '8px 0',
              fontStyle: 'italic',
              color: patientAccessTheme.palette.text.mediumemphasis,
            }}
            {...props}
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

const PlanDocumentDetailsPage: React.FC<PlanDocumentDetailsPageProps> = observer(
  () => {
    //constants
    const { classes } = useStyles();
    const messages = SummaryOfBenefitsConstants;
    const employerGroupStore = useStores().employerGroupStore;
    const loginPageStore = useStores().loginPageStore;
    const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
    const employer =
      employerGroupStore.employerGroupResponseData.employer_group_name[0];
    // const contractAiqStore = useStores().contractAiqStore;
    const navigate = useNavigate();
    const location: any = useLocation();
    const { planNames, planId, docType, isReportsTab } = location?.state;
    const unMatchingExclusionColumns: Columns[] = [
      {
        name: 'Exclusion Category',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '400px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'exclusion_name',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Code',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '150px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'code',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Code Type',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '150px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'code_type',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Description',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '435px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'description',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
    ];

    const matchingExclusionColumns: Columns[] = [
      {
        name: 'Description',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '250px',
        cellPadding: '8px 18px 8px 46px',
        showThisColumn: true,
        sortColumnName: 'description',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Exclusion Category',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '200px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'exclusion_name',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Code',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '120px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'code',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },
      {
        name: 'Code Type',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '120px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'code_type',
        DropDownMenuItems: dropDownMenuItemsAlphabets,
      },

      {
        name: 'Members',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '100px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'members',
        DropDownMenuItems: dropDownMenuItemsValues,
      },
      {
        name: 'Claims',
        align: 'left',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '100px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'claims',
        DropDownMenuItems: dropDownMenuItemsValues,
      },
      {
        name: 'Total Plan Paid',
        align: 'right',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '130px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'total_plan_paid',
        DropDownMenuItems: dropDownMenuItemsValues,
      },
      {
        name: 'Total Member Paid',
        align: 'right',
        cellStyles: {
          ...cellStyles1,
          height: '38px',
        },
        width: '180px',
        cellPadding: '16px',
        showThisColumn: true,
        sortColumnName: 'total_mem_plan_paid',
        DropDownMenuItems: dropDownMenuItemsValues,
      },
    ];
    const breadcrumbsItemsList = [
      [
        'text',
        'Plan Documents',
        {
          color: patientAccessTheme.palette.primary[500],
          fontWeight: 500,
        },
        () => {
          navigate(PLAN_DOCUMENTS);
        },
      ],
      [
        'text',
        `${
          docType === 'SPD' ? 'Summary Plan Description' : 'Summary of Benefits'
        }`,
        {
          color: patientAccessTheme.palette.text.highemphasis,
          fontWeight: 600,
        },
      ],
    ];
    const coverageCheckFilterOptions = [
      'Claims Governance & Adjudication',
      'Eligibility',
      'Exclusion',
      'Plan Design',
      'Preauthorization Requirements',
      'Special Care Programs',
      'Vendor',
    ];

    const customButtonVariant: Array<'contained' | 'outlined'> = [];
    const customButtonClick: (() => void)[] = [];
    const customButtonWidth: string[] = [];
    const customButtonText: string[] = [];

    const matrixColumns: MatrixColumns[] = [
      {
        header: 'Provision Name',
        accessor: 'provision',
        isSortEnabled: true,
        cell: (value: any, row: any) => (
          <div className={classes.provisionGrid}>
            <div>{value}</div>
            {row?.tooltip?.description && (
              <Tooltip
                title={
                  <>
                    <Typography
                      fontSize="13px"
                      lineHeight="19.5px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.status.white}
                    >
                      {row?.tooltip?.description}
                    </Typography>
                    <Typography
                      fontSize="13px"
                      lineHeight="19.5px"
                      fontType="regular"
                      textColor={patientAccessTheme.palette.status.white}
                      margin="8px 0px 0px 0px"
                    >
                      {row?.tooltip?.explaination?.replace(/\?/g, '')}
                    </Typography>
                  </>
                }
                placement="bottom"
              >
                <div className={classes.iconWrapper}>
                  <SvgInfo />
                </div>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        header: 'Value',
        accessor: 'value',
        cell: getBooleanCell,
        align: 'center',
      },
    ];

    //states
    const [pageLoading, setPageLoading] = useState<boolean>(true);

    const [docLoading, setDocLoading] = useState<boolean>(false);
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(
      location?.state?.tabIndex ?? 0,
    );
    const [loader] = useState<any>(<Loader />);
    const [tabs, setTabs] = useState<string[]>([]);
    const [planDetails, setPlanDetails] = useState<any>({});
    //Documents
    const [documents, setDocuments] = useState<any>([]);
    const [selectedDoc, setSelectedDoc] = useState<any>();
    //Reports
    const [reportData, setReportData] = useState<any>([]);
    const [reportsLoading, setReportsLoading] = useState<boolean>(true);
    //Exclusions Analysis
    const [matExclLoading, setMatExclLoading] = useState<boolean>(true);
    const [unMatExclLoading, setUnMatExclLoading] = useState<boolean>(true);
    const [matchingExclusionsData, setMatchingExclusionsData] = useState<{
      exclusions: any[];
      totalCount: number;
      totalPlanPaid: number;
      totalClaims: number;
    }>();
    const [matExclPageNumber, setMatExclPageNumber] = useState<number>(1);
    const [matExclTotalPages, setMatExclTotalPages] = useState<number>(0);
    const [matExclSortField, setMatExclSortField] = useState<string>(
      'total_plan_paid',
    );
    const [matExclSortOrder, setMatExclSortOrder] = useState<number>(-1);
    const [columnId, setColumnId] = useState<string>();

    const [unMatchingExclusionsData, setUnMatchingExclusionsData] = useState<
      any[]
    >([]);
    const [unMatExclPageNumber, setUnMatExclPageNumber] = useState<number>(1);
    const [unMatExclTotalPages, setUnMatExclTotalPages] = useState<number>(0);
    const [unMatExclSortField, setUnMatExclSortField] = useState<string>(
      'exclusion_name',
    );
    const [unMatExclSortOrder, setUnMatExclSortOrder] = useState<number>(-1);

    //Coverage Check
    const [filteredMatrixData, setFilteredMatrixData] = useState<any[]>([]);
    const [matrixSortConfig, setMatrixSortConfig] = useState<{
      fieldName: string;
      direction: 'asc' | 'desc' | null;
    }>({ fieldName: 'provision', direction: 'asc' });
    const [coverageCheckLoading, setCoverageCheckLoading] = useState<boolean>(
      true,
    );
    const [filteredTopics, setFilteredTopics] = useState<string[]>([]);

    //Matrix States
    const [provisions, setProvisions] = useState<any[]>([]);
    const [redFlags, setRedFlags] = useState<any[]>([]);
    const [missingChecks, setMissingChecks] = useState<any[]>([]);
    const [flaggedOnly, setFlaggedOnly] = useState<true | false | null>(null);
    const [missingOnly, setMissingOnly] = useState<true | false | null>(null);
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [switchValue, setSwitchValue] = useState<boolean>(true);
    const [redFlagsFilters, setRedFlagsFilters] = useState<string[]>([]);
    const [missingChecksFilters, setMissingChecksFilters] = useState<string[]>(
      [],
    );
    const [legalTypeFilter, setLegalTypeFilter] = useState<any>([]);
    const [legalFilters, setLegalFilters] = useState<string[]>([]);
    const [importanceFilters, setImportanceFilters] = useState<string[]>([]);
    const [dataByCategory, setDataByCategory] = useState<{
      'Red Flag Checks': any[];
      'Missing Checks': any[];
    }>({
      'Red Flag Checks': [],
      'Missing Checks': [],
    });
    const [provisionsBySections, setProvisionsBySections] = useState<any>([]);
    const [legalDoctrineOptions, setLegalDoctrineOptions] = useState<string[]>(
      [],
    );
    const [legalCitationOptions, setLegalCitationOptions] = useState<string[]>(
      [],
    );

    const filterOptions = React.useMemo(() => {
      const getClauses = (category: string): string[] =>
        Array.from(
          new Set(
            provisions
              ?.filter((item) => item.provisionsCategory === category)
              ?.map((item) => item.clause.trim())
              ?.filter(Boolean),
          ),
        );

      return {
        'Red Flag Checks': getClauses('Red Flag Checks'),
        'Missing Checks': getClauses('Missing Checks'),
      };
    }, [provisions]);

    const importanceFilterOptions = React.useMemo(() => {
      const getImportance = (category: string): string[] =>
        Array.from(
          new Set(
            provisions
              ?.filter((item) => item.provisionsCategory === category)
              ?.flatMap((item) => {
                const importance = item?.importance;

                if (Array.isArray(importance)) {
                  return importance.map((i: any) => i?.trim()).filter(Boolean);
                }

                if (typeof importance === 'string') {
                  return importance
                    .split(',')

                    .map((i: any) => i.trim())
                    .filter(Boolean);
                }

                return [];
              }),
          ),
        );

      return {
        'Red Flag Checks': getImportance('Red Flag Checks'),
        'Missing Checks': getImportance('Missing Checks'),
      };
    }, [provisions]);

    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
    const [
      documentDetails,
      setDocumentDetails,
    ] = useState<DocumentDetailsDataType>({
      documentUrl: '',
      chunks: [],
      pageNumber: 0,
    });
    const [openDocument, setOpenDocument] = useState<boolean>(false);

    // Chat states
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatLoading, setChatLoading] = useState<boolean>(false);
    // const [chatHistoryLoading, setChatHistoryLoading] = useState<boolean>(
    //   false,
    // );
    const [isThinking, setIsThinking] = useState<boolean>(false);

    if (
      loginPageStore.userResponse.role.name === UserAccessConstants.SUPER_ADMIN
    ) {
      customButtonVariant.push('contained');
      customButtonClick.push(() => handleRegenerateMetaData());
      customButtonWidth.push('200px');
      customButtonText.push(messages.REGENERATE_METADATA);
    }

    // Add "View Document" button
    customButtonVariant.push('outlined');
    customButtonClick.push(() => {
      setSelectedTabIndex(tabs.indexOf('Documents'));
    });
    customButtonWidth.push('160px');
    customButtonText.push('Document View');

    //functions
    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };

    const handleCloseSnackBar = (
      event?: React.SyntheticEvent,
      reason?: string,
    ) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    const handleSelectedIndex = (index: number) => {
      setSelectedTabIndex(index);
    };

    const handleMatExclPageChange = (
      event: ChangeEvent<unknown>,
      page: number,
    ) => {
      setMatExclPageNumber(page);
    };

    const handleUnMatExclPageChange = (
      event: ChangeEvent<unknown>,
      page: number,
    ) => {
      setUnMatExclPageNumber(page);
    };

    const handleMatchingSort = (event: any, column: any, item: any) => {
      const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
      const { sortColumnName } = column;
      if (!sortColumnName) {
        return;
      }

      setMatExclSortField(sortColumnName);
      setMatExclSortOrder(sortOrder);
    };

    const handleUnMatchingSort = (event: any, column: any, item: any) => {
      const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
      const { sortColumnName } = column;
      if (!sortColumnName) {
        return;
      }

      setUnMatExclSortField(sortColumnName);
      setUnMatExclSortOrder(sortOrder);
    };

    const reportHandleOnClick = (
      reportName: string,
      planDocId?: string,
      compareId?: string,
      status?: any,
    ) => {
      let path;
      if (status === 'Ready') {
        switch (reportName) {
          case 'ERISA Regulatory Compliance Check':
            path = REGULATORY_COMPLIANCE_REPORT;
            break;
          case 'Comparison with Administrative Service Agreement':
          case 'Gap Analysis':
          case 'Comparison with PBM Service Agreement':
            path = INTER_CONTRACT_REPORT;
            break;
          default:
            return; // Exit if reportName doesn't match any case
        }
        navigate(path, {
          state: {
            reportName,
            document: `${employerGroupStore?.employerGroupResponseData?.employer_group_name} - Plan Document`,
            tabIndex: selectedTabIndex,
            planDocId: planDocId,
            compareId: compareId,
          },
        });
      }
    };

    const handleRegenerateReport = async (
      reportName: string,
      planId: string,
      compareId: string,
    ) => {
      const egNid = employerGroupStore.employerGroupResponseData.eg_nid[0];
      const employer =
        employerGroupStore.employerGroupResponseData.employer_group_name[0];

      handleOpenSnackBar(
        'Report re-generation is in progress.To view the regenerated report please come again after some time',
        'info',
        6000,
      );

      const planDocUrl: string = selectedDoc?.original_url;
      // masterDocuments?.find(
      //   (doc: any) => doc.plan === selectedPlan?.plan_type,
      // )?.documentUrl || '';

      let compareUrl: string = '';
      if (compareId) {
        const response = await axios.post(
          `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.getContractDetails}`,
          { contract_id: compareId },
        );
        if (response.status !== 200) {
          throw new Error('Failed to fetch contracts');
        }
        const data = response.data?.data[0];
        const masterDocument =
          data?.documents.find(
            (doc: any) => doc.document_type === 'Master Document',
          ) ||
          data?.documents.find(
            (doc: any) => doc.document_type === 'Base Document',
          );
        compareUrl = masterDocument?.document_url;
      }

      await extractPlanDocsReport(
        planId,
        planDocUrl,
        reportName,
        egNid,
        compareId,
        compareUrl,
        employer,
      );
      getMatrixData();
      handleOpenSnackBar(
        'Report has been regenerated successfully',
        'success',
        6000,
      );
    };

    const handleRegenerateMetaData = async () => {
      const documentUrl = selectedDoc?.original_url;
      handleOpenSnackBar(
        'Metadata re-generation is in progress.To view the regenerated metadata please come again after some time',
        'info',
        6000,
      );

      const allowedFeatures = ['Red Flag Checks', 'Missing Checks'];

      for (const feature of allowedFeatures) {
        await extractAndSaveProvisionMatrixData(
          documentUrl,
          '',
          egNid,
          employer,
          'Summary Plan Document',
          getCategoryByFeature(feature),
          planId,
          '',
        );
      }
      handleOpenSnackBar(
        'Metadata has been regenerated successfully',
        'success',
        6000,
      );
    };

    const getMemberClaims = async (claim_id_list: string[]) => {
      return await axios
        .post(
          properties.aiqBaseUrl +
            properties.membersBaseUrl +
            properties.memberDetails,
          {
            eg_nid: [egNid],
            startDate:
              moment(planDetails?.effective_date)?.format('YYYY-MM-DD') || '',
            endDate: moment()?.format('YYYY-MM-DD') || '',
            member_unique_id: [],
            claim_id_list: claim_id_list,
          },
        )
        .then((response) => response.data);
    };

    const formatMatchingExclusions = async (data: any) => {
      const updatedData = await Promise.all(
        data?.map(async (item: any) => {
          const memberClaimsData = await getMemberClaims(item?.claim_ids);
          return {
            ...item,
            claimData: memberClaimsData?.data,
          };
        }),
      );

      const tableData = updatedData?.map((item) => [
        getTextWithToolTip(item?.description, 20, {
          color: patientAccessTheme.palette.text.mediumemphasis,
          fontWeight: 500,
          fontSize: '14px',
        }),
        getTextWithToolTip(item?.exclusion_name, 20, {
          color: patientAccessTheme.palette.text.mediumemphasis,
          fontWeight: 500,
          fontSize: '14px',
        }),

        item?.code,
        item?.code_type,
        item?.member_unique_ids?.length,
        item?.claim_ids?.length,
        '$' + commasForNumbers(item?.total_plan_paid?.toString(), 2),
        '$' + commasForNumbers(item?.total_mem_plan_paid?.toString(), 2),
        <MedicalHistoryDetails
          riskPatientDetails={formatMemberClaim(item?.claimData)}
        />,
      ]);
      return tableData;
    };

    const handleMatrixSort = (fieldName: string) => {
      let direction: 'asc' | 'desc' = 'asc';
      if (
        matrixSortConfig?.fieldName === fieldName &&
        matrixSortConfig.direction === 'asc'
      ) {
        direction = 'desc';
      }
      setMatrixSortConfig({ fieldName: fieldName, direction });

      const sorted = [...filteredMatrixData].sort((a, b) => {
        if (a[fieldName] < b[fieldName]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a[fieldName] > b[fieldName]) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      });

      setFilteredMatrixData(sorted);
    };

    const handleRegenerate = (reportName: string) => {
      const planDoc: any = planDetails?.documents?.find(
        (doc: any) =>
          doc?.document_type === 'Base Document' ||
          doc?.document_type === 'Master Document',
      );
      extractPlanDocsReport(
        planId,
        planDoc?.original_url || '',
        reportName,
        egNid,
        '',
        '',
        employer,
        planDoc?.markdown_url,
      );
    };

    const onClickReference = async (chunks: any[], pageNumber: number) => {
      let documentUrl = selectedDoc?.document_url ?? null;
      if (!documentUrl) {
        documentUrl = await createBlobUrl(selectedDoc?.original_url);
      }
      setDocumentDetails({ documentUrl, chunks, pageNumber });
      setOpenDocument(true);
    };

    const handleChunkClick = (rowData: any[], matrixData: any[]) => {
      const rawChunkLabel = rowData?.[0] ?? '';

      const chunkName = rawChunkLabel.startsWith('Chunk')
        ? rawChunkLabel.replace(/^Chunk\s*\d+\s*-\s*/, '')
        : rawChunkLabel;
      for (const pageItem of matrixData) {
        const matchedChunk = pageItem?.chunks?.find(
          (chunk: any) => chunk?.chunkName === chunkName,
        );

        if (matchedChunk) {
          const groundingData = matchedChunk?.groundingData || [];
          const page = pageItem?.page;
          onClickReference(groundingData, page);
          return;
        }
      }
    };

    const resetRedFlagFilters = (resetKey = false) => {
      setFlaggedOnly(null);
      setRedFlagsFilters([]);
      setLegalFilters([]);
      setLegalTypeFilter(null);
      setImportanceFilters([]);
      // setImpactFilters([]);
      setRedFlags(dataByCategory['Red Flag Checks']);
      if (resetKey) {
        setFilterResetKey((prev) => prev + 1);
      }
    };
    const resetMissingFlagFilters = (resetKey = false) => {
      setMissingOnly(null);
      setMissingChecksFilters([]);
      setLegalFilters([]);
      setLegalTypeFilter(null);
      setImportanceFilters([]);
      // setImpactFilters([]);
      setMissingChecks(dataByCategory['Missing Checks']);
      if (resetKey) {
        setFilterResetKey((prev) => prev + 1);
      }
    };

    const getToggle = (category: string) => {
      switch (category) {
        case 'Red Flag Checks':
          return (
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DynamicFilter
                filterOptions={[
                  {
                    label: 'Flagged Issues',
                    options: ['Flagged', 'Not Flagged'],
                    selected:
                      flaggedOnly === null
                        ? ''
                        : flaggedOnly === true
                        ? 'Flagged'
                        : 'Not Flagged',
                    onChange: (value: string) => {
                      resetRedFlagFilters();
                      if (value === 'Flagged') {
                        setFlaggedOnly(true);
                      } else {
                        setFlaggedOnly(false);
                      }
                    },
                  },
                  {
                    label: 'Clause Topic',
                    options: filterOptions[category],
                    selected: redFlagsFilters,
                    isSelectMultiple: true,
                    onChange: (values: string[]) => {
                      resetRedFlagFilters();
                      setRedFlagsFilters(values);
                      // Apply filters immediately
                      const filteredData = [
                        ...dataByCategory['Red Flag Checks'],
                      ];
                      let filteredProvisions = [...filteredData];

                      if (values?.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) =>
                            values.includes(provision?.clause),
                        );
                      }

                      setRedFlags(filteredProvisions);
                    },
                  },
                  {
                    label: 'Legal Type',
                    options: [],
                    selected: null,
                    levels: [
                      {
                        label: 'Legal Type',
                        options: ['Legal Doctrine', 'Legal Citation'],
                      },
                      {
                        label: 'Select Options',
                        options: (previousValue: string) => {
                          if (previousValue === 'Legal Doctrine') {
                            return legalDoctrineOptions;
                          } else if (previousValue === 'Legal Citation') {
                            return legalCitationOptions;
                          }
                          return [];
                        },
                        isMultiSelect: true,
                      },
                    ],

                    onChange: (selections: string[]) => {
                      const [filterType, selectedValues] = selections;

                      const values = selectedValues
                        ? selectedValues.split(',').map((v) => v.trim())
                        : [];

                      setLegalTypeFilter(filterType);
                      setLegalFilters(values);

                      const filteredData = [
                        ...dataByCategory['Red Flag Checks'],
                      ];

                      let filteredProvisions = [...filteredData];

                      if (filterType && values.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) => {
                            let fieldValues: string[] = [];

                            if (filterType === 'Legal Doctrine') {
                              fieldValues = provision.legal_doctorine ?? [];
                            }

                            if (filterType === 'Legal Citation') {
                              fieldValues = provision.legal_citation ?? [];
                            }

                            return fieldValues.some((val) =>
                              values.includes(val),
                            );
                          },
                        );
                      }

                      setRedFlags(filteredProvisions);
                    },
                  },
                  {
                    label: 'Importance',
                    options: importanceFilterOptions[category],
                    selected: importanceFilters,
                    isSelectMultiple: true,
                    onChange: (values: string[]) => {
                      setImportanceFilters(values);
                      const filteredData = [
                        ...dataByCategory['Red Flag Checks'],
                      ];
                      let filteredProvisions = [...filteredData];
                      if (values?.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) => {
                            const importance = Array.isArray(
                              provision.importance,
                            )
                              ? provision.importance
                              : typeof provision.importance === 'string'
                              ? provision.importance
                                  .split(',')
                                  .map((i: string) => i.trim())
                              : [];

                            return importance.some((importance: string) =>
                              values.includes(importance),
                            );
                          },
                        );
                      }
                      setRedFlags(filteredProvisions);
                    },
                  },
                ]}
                resetKey={filterResetKey}
              />
              <Button
                label={'Reset Filters'}
                onClick={() => {
                  resetRedFlagFilters(true);
                }}
              />
            </Grid>
          );
        case 'Missing Checks':
          return (
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DynamicFilter
                filterOptions={[
                  {
                    label: 'Missing Checks',
                    options: ['Included', 'Excluded'],
                    selected:
                      missingOnly === null
                        ? ''
                        : missingOnly === true
                        ? 'Included'
                        : 'Excluded',
                    onChange: (value: string) => {
                      resetMissingFlagFilters();
                      if (value === 'Included') {
                        setMissingOnly(true);
                      } else {
                        setMissingOnly(false);
                      }
                    },
                  },
                  {
                    label: 'Clause Topic',
                    options: filterOptions[category],
                    selected: missingChecksFilters,
                    isSelectMultiple: true,
                    onChange: (values: string[]) => {
                      resetMissingFlagFilters();
                      setMissingChecksFilters(values);
                      // Apply filters immediately
                      const filteredData = [
                        ...dataByCategory['Missing Checks'],
                      ];
                      let filteredProvisions = [...filteredData];

                      if (values?.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) =>
                            values.includes(provision?.clause),
                        );
                      }
                      setMissingChecks(filteredProvisions);
                    },
                  },
                  {
                    label: 'Legal Type',
                    options: [],
                    selected: null,
                    levels: [
                      {
                        label: 'Legal Type',
                        options: ['Legal Doctrine', 'Legal Citation'],
                      },
                      {
                        label: 'Select Options',
                        options: (previousValue: string) => {
                          if (previousValue === 'Legal Doctrine') {
                            return legalDoctrineOptions;
                          } else if (previousValue === 'Legal Citation') {
                            return legalCitationOptions;
                          }
                          return [];
                        },
                        isMultiSelect: true,
                      },
                    ],

                    onChange: (selections: string[]) => {
                      const [filterType, selectedValues] = selections;

                      const values = selectedValues
                        ? selectedValues.split(',').map((v) => v.trim())
                        : [];

                      setLegalTypeFilter(filterType);
                      setLegalFilters(values);

                      const filteredData = [
                        ...dataByCategory['Missing Checks'],
                      ];

                      let filteredProvisions = [...filteredData];

                      if (filterType && values.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) => {
                            let fieldValues: string[] = [];

                            if (filterType === 'Legal Doctrine') {
                              fieldValues = provision.legal_doctorine ?? [];
                            }

                            if (filterType === 'Legal Citation') {
                              fieldValues = provision.legal_citation ?? [];
                            }

                            return fieldValues.some((val) =>
                              values.includes(val),
                            );
                          },
                        );
                      }

                      setMissingChecks(filteredProvisions);
                    },
                  },
                  {
                    label: 'Importance',
                    options: importanceFilterOptions[category],
                    selected: importanceFilters,
                    isSelectMultiple: true,
                    onChange: (values: string[]) => {
                      setImportanceFilters(values);
                      const filteredData = [
                        ...dataByCategory['Missing Checks'],
                      ];
                      let filteredProvisions = [...filteredData];
                      if (values?.length) {
                        filteredProvisions = filteredData.filter(
                          (provision: MatrixData) => {
                            const impacts = Array.isArray(provision.importance)
                              ? provision.importance
                              : typeof provision.importance === 'string'
                              ? provision.importance
                                  .split(',')
                                  .map((i: string) => i.trim())
                              : [];

                            return impacts.some((importance: string) =>
                              values.includes(importance),
                            );
                          },
                        );
                      }
                      setMissingChecks(filteredProvisions);
                    },
                  },
                ]}
                resetKey={filterResetKey}
              />
              <Button
                label={'Reset Filters'}
                onClick={() => {
                  resetMissingFlagFilters(true);
                }}
              />
            </Grid>
          );
        default:
          return undefined;
      }
    };

    const renderMatrixByCategory = (
      category: 'Red Flag Checks' | 'Missing Checks',
      selectedFilters: string[],
      setSelectedFilters: React.Dispatch<React.SetStateAction<string[]>>,
      matrixData: any[],
    ) => {
      let filteredData = [...matrixData];
      if (switchValue) {
        const filters = {
          flaggedOnly,
          missingOnly,
          selectedFilters,
          legalTypeFilter,
          legalFilters,
          importanceFilters,
        };
        filteredData = formatMatrixData(matrixData, category, filters);
      } else {
        if (category === 'Red Flag Checks' && flaggedOnly) {
          filteredData = filteredData.filter((item) => item.value === true);
        }

        if (category === 'Missing Checks' && missingOnly) {
          filteredData = filteredData.filter((item) => item.value !== true);
        }

        filteredData = filteredData.map((item) => {
          let displayValue = item.value;

          const priorityOrder = ['Introduction', 'Term'];

          filteredData.sort((a, b) => {
            const aIndex = priorityOrder.indexOf(a.category);
            const bIndex = priorityOrder.indexOf(b.category);

            // If both categories are in the priority list, follow that order
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }

            // If only one is in the list, it comes first
            if (aIndex !== -1) {
              return -1;
            }
            if (bIndex !== -1) {
              return 1;
            }

            // Otherwise, sort alphabetically among the rest
            return a.category.localeCompare(b.category);
          });

          switch (category) {
            case 'Red Flag Checks':
              displayValue = item.value === true ? 'Flagged' : 'No';
              break;
            case 'Missing Checks':
              displayValue = item.value === true ? 'Yes' : 'Missing';
              break;
            default:
              filteredData = filteredData;
          }

          return {
            ...item,
            value: (
              <Box
                key={`${item.provision_name}-value`}
                className={classes.parameterBox}
              >
                <Typography fontSize="14px" lineHeight="24px" component="div">
                  <span>
                    {typeof displayValue === 'string'
                      ? displayValue.charAt(0).toUpperCase() +
                        displayValue.slice(1)
                      : displayValue}
                  </span>
                  {item?.chunks?.length > 0 &&
                    Array.from(
                      new Map(
                        item?.chunks?.map((ref: any) => {
                          const key =
                            ref?.grounding?.extractions?.reference ??
                            ref?.grounding?.page;

                          return [key, ref];
                        }),
                      ).values(),
                    ).map((ref: any, ind: number, arr: any[]) => {
                      const reference = ref?.grounding?.extractions?.reference;
                      const page = ref?.grounding?.page;

                      if (reference || (page !== null && page !== undefined)) {
                        return (
                          <DottedText
                            key={ref.chunk_id}
                            text={
                              reference
                                ? `${reference}`
                                : `PgNo:${page + 1}${
                                    ind < arr.length - 1 ? ',' : ''
                                  }`
                            }
                            styles={{ paddingLeft: '5px' }}
                            onClick={() => onClickReference(item.chunks, page)}
                          />
                        );
                      }

                      return null;
                    })}
                </Typography>
              </Box>
            ),
          };
        });
      }

      return (
        <ProvisionMatrix
          title={category}
          matrixColumns={matrixColumns}
          matrixData={filteredData}
          showFilters={false}
          showRegenerate={false}
          showSwitch={true}
          switchValue={switchValue}
          setSwitchValue={setSwitchValue}
          handleChunkClick={(rowData) =>
            handleChunkClick(rowData, filteredData)
          }
          toggle={getToggle(category)}
        />
      );
    };

    const getTabsContent = () => {
      switch (tabs[selectedTabIndex]) {
        case 'Documents':
          return <ViewDocument />;
        case 'Reports':
          return <Reports />;
        case 'Exclusion Analysis':
          return <PlanExclusionsAnalysis />;

        case 'Red Flag Checks':
          return coverageCheckLoading
            ? loader
            : renderMatrixByCategory(
                'Red Flag Checks',
                redFlagsFilters,
                setRedFlagsFilters,
                switchValue ? provisionsBySections : redFlags,
              );
        case 'Missing Checks':
          return coverageCheckLoading
            ? loader
            : renderMatrixByCategory(
                'Missing Checks',
                missingChecksFilters,
                setMissingChecksFilters,
                switchValue ? provisionsBySections : missingChecks,
              );
        case 'Coverage Check':
        case 'Overview':
          return (
            <>
              {coverageCheckLoading ? (
                loader
              ) : (
                <>
                  {loginPageStore.userResponse.role.name ===
                    UserAccessConstants.SUPER_ADMIN && (
                    <Grid style={{ marginBottom: '10px' }}>
                      <Button
                        label={'Regenerate'}
                        variant="contained"
                        height="37px"
                        width="auto"
                        onClick={() => handleRegenerate('Coverage Check')}
                      />
                    </Grid>
                  )}
                  <CoverageCheck
                    data={filteredMatrixData}
                    handleSort={handleMatrixSort}
                    sortConfig={matrixSortConfig}
                    filterOptions={coverageCheckFilterOptions}
                    selectedFilterOptions={filteredTopics}
                    setSelectedFilterOptions={setFilteredTopics}
                    // handleAddColumns={handleAddFilters}
                  />
                </>
              )}
            </>
          );
        default:
          return <></>;
      }
    };

    // const handleAddFilters = () => {
    //   let covergaMatrixData = [...matrixData];
    //   if (filteredTopics?.length) {
    //     covergaMatrixData = [...matrixData]?.filter((provision: any) =>
    //       filteredTopics?.includes(provision?.topic),
    //     );
    //   }
    //   setFilteredMatrixData(covergaMatrixData);
    // };

    // const formatMatrixData = (data: any[]) => {
    //   return data?.map((item: any) => ({
    //     provision: item?.provision_name,
    //     favorabilityRange: getName(item?.favorability),
    //     value: item?.value,
    //     background: {
    //       value: getProvisonCellBackground(item?.favorability, item?.value),
    //     },
    //     tooltip: {
    //       description: item?.assessment_description,
    //       explaination: item?.explanation,
    //       value: item?.evidence ? item?.evidence[0] : '',
    //     },
    //     topic: item?.topic,
    //   }));
    // };

    //APIs

    const formatDocumentsUrl = async (data: any[]) => {
      if (data) {
        // Create a new array with updated document blob URLs
        const updatedResponse = await Promise.all(
          data?.map(async (item: any) => {
            const updatedDocuments = await Promise.all(
              item?.documents?.map(async (doc: any) => {
                const newDocumentUrl = await createBlobUrl(doc.document_url);
                return {
                  ...doc,
                  original_url: doc.document_url,
                  document_url: newDocumentUrl,
                  markdown_url: doc.markdown_url,
                };
              }),
            );
            return { ...item, documents: updatedDocuments };
          }),
        );

        return updatedResponse;
      } else {
        return [];
      }
    };

    //Axios Calls
    const getPlanDetails = async () => {
      setPageLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.getPlanDocs,
          {
            eg_nid: egNid,
            plan_ids: [planId],
          },
        )
        .then(async (res: any) => {
          if (res.status === 200) {
            const data = res?.data?.data;
            const updatedResponse = await formatDocumentsUrl(data);

            const tabSet = new Set<string>();

            const reports = data[0]?.reports || [];

            if (
              reports?.includes('Coverage Check') ||
              reports?.includes('Overview')
            ) {
              tabSet.add('Red Flag Checks');
              tabSet.add('Missing Checks');
            }
            if (reports.includes('Exclusion Analysis')) {
              tabSet.add('Exclusion Analysis');
            }

            if (
              [
                'ERISA Regulatory Compliance Check',
                'Comparison with Administrative Service Agreement',
                'Gap Analysis',
                'Comparison with PBM Service Agreement',
              ].some((report) => reports?.includes(report))
            ) {
              tabSet.add('Reports');
            }

            if (data[0]?.documents) {
              tabSet.add('Documents');
            }

            // Apply all found tabs
            setTabs(Array.from(tabSet));
            setSelectedTabIndex(
              isReportsTab
                ? Array.from(tabSet).indexOf('Reports')
                : selectedTabIndex,
            );
            setPlanDetails(updatedResponse[0]);
            setDocuments(updatedResponse[0]?.documents);
            setPageLoading(false);
          }
        })
        .catch((error) => {
          throw error;
        });
    };

    const getReports = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            '/get-spd-reports',
          {
            eg_nid: egNid,
            plan_id: planId,
          },
        )
        .then(async (res) => {
          if (res?.status === 200) {
            // contractAiqStore.setSpdAgreementId(
            //   res?.data?.data?.[0]?.spd_agreement_id,
            // );
            setReportData(res?.data?.data);
          }
        })
        .catch((error) => {
          return error;
        });
      setReportsLoading(false);
    };

    const getMatchingPlanExclusions = async () => {
      setMatExclLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            '/get-plan-exclusions',
          {
            eg_nid: egNid,
            plan_id: planId,
            claimsIdentified: true,
            page: matExclPageNumber,
            sortField: matExclSortField,
            sortOrder: matExclSortOrder,
            startDate:
              moment(planDetails?.effective_date)?.format('YYYY-MM-DD') || '',
            endDate: moment()?.format('YYYY-MM-DD') || '',
          },
        )
        .then(async (res) => {
          if (res?.status === 200) {
            const data = res?.data?.data;
            const { exclusions, totalCount, totalPlanPaid, totalClaims } = data;
            setMatExclTotalPages(Math.ceil(totalCount / 10));
            const matchingExclusions: any[] = await formatMatchingExclusions(
              exclusions,
            );
            setMatchingExclusionsData({
              exclusions: matchingExclusions,
              totalCount,
              totalPlanPaid,
              totalClaims,
            });
          }
          setMatExclLoading(false);
        })
        .catch((error) => {
          return error;
        });
    };

    const getUnMatchingPlanExclusions = async () => {
      setUnMatExclLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            '/get-plan-exclusions',
          {
            eg_nid: egNid,
            plan_id: planId,
            claimsIdentified: false,
            page: unMatExclPageNumber,
            sortField: unMatExclSortField,
            sortOrder: unMatExclSortOrder,
            startDate:
              moment(planDetails?.effective_date)?.format('YYYY-MM-DD') || '',
            endDate: moment()?.format('YYYY-MM-DD') || '',
          },
        )
        .then(async (res) => {
          if (res?.status === 200) {
            const data = res?.data?.data;
            const { exclusions, totalCount } = data;
            setUnMatExclTotalPages(Math.ceil(totalCount / 10));

            const unMatchingExclusions = exclusions?.map((item: any) => [
              getTextWithToolTip(item?.exclusion_name, 50, {
                color: patientAccessTheme.palette.text.mediumemphasis,
                fontWeight: 500,
                fontSize: '14px',
              }),
              item?.code,
              item?.code_type,
              item?.description,
            ]);
            setUnMatchingExclusionsData(unMatchingExclusions);
          }
          setUnMatExclLoading(false);
        })
        .catch((error) => {
          return error;
        });
    };
    const getMatrixDataBySection = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.getMatrixByProvisions,
          {
            plan_id: planId,
            eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
            document_url:
              planDetails?.documents?.find(
                (doc: any) =>
                  doc.document_type === 'Master Document' ||
                  doc.document_type === 'Base Document',
              )?.original_url || '',
          },
        )
        .then((response) => {
          if (response.status === 200) {
            setProvisionsBySections(response.data.data);
          }
        })
        .catch((error) => {
          handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
        });
    };

    const getMatrixData = async () => {
      planId
        ? await axios
            .post(
              properties.aiqBaseUrl + properties.contractBaseUrl + '/matrix',
              {
                plan_id: planId,
                eg_nids: employerGroupStore.employerGroupResponseData.eg_nid,
                document_url:
                  planDetails?.documents?.find(
                    (doc: any) =>
                      doc.document_type === 'Master Document' ||
                      doc.document_type === 'Base Document',
                  )?.original_url || '',
              },
            )
            .then((response) => {
              if (response.status === 200) {
                const data = response?.data?.data[0];

                const provisions = data?.matrix_data;
                const redFlags = formatNewProvisions(
                  provisions?.filter(
                    (temp: any) =>
                      temp.category === 'Red Flag Checks' &&
                      (temp.deleted === false || temp.deleted === undefined),
                  ),
                );
                const missingChecks = formatNewProvisions(
                  provisions?.filter(
                    (temp: any) =>
                      temp.category === 'Missing Checks' &&
                      (temp.deleted === false || temp.deleted === undefined),
                  ),
                );
                setDataByCategory({
                  'Red Flag Checks': redFlags,
                  'Missing Checks': missingChecks,
                });
                setProvisions(formatNewProvisions(provisions));
                setRedFlags(redFlags);
                setMissingChecks(missingChecks);
                setCoverageCheckLoading(false);
              }
            })
            .catch((error) => {
              handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
            })
        : setCoverageCheckLoading(false);
    };

    const fetchFiduciaryList = async () => {
      const result = await axios.get(
        properties.aiqBaseUrl +
          properties.playbookAndStandard +
          properties.getFiduciaryList,
        // {
        //   params: { eg_nid: egNid },
        // },
      );
      const data = result.data?.data || {};

      setLegalDoctrineOptions(data.legal_doctrine || []);
      setLegalCitationOptions(data.legal_citation || []);
    };

    const fetchApis = async () => {
      switch (tabs[selectedTabIndex]) {
        case 'Reports':
          await getReports();
          break;
        case 'Coverage Check':
        case 'Overview':
        case 'Red Flag Checks':
        case 'Missing Checks':
          // await getMatrixData();
          break;
        default:
          break;
      }
    };

    const defaultAPICalls = async () => {
      // await getPlanDocuments();
      await getPlanDetails();
    };

    // useEffects
    useEffect(() => {
      if (!egNid) {
        return;
      }
      defaultAPICalls();
      fetchFiduciaryList();
    }, [egNid]);

    useEffect(() => {
      if (!egNid) {
        return;
      }
      fetchApis();
    }, [selectedTabIndex, tabs, egNid]);

    //useEffects
    useEffect(() => {
      tabs[selectedTabIndex] === 'Exclusion Analysis' &&
        egNid &&
        getMatchingPlanExclusions();
    }, [
      matExclPageNumber,
      matExclSortField,
      matExclSortOrder,
      selectedTabIndex,
      egNid,
    ]);

    //useEffects
    useEffect(() => {
      tabs[selectedTabIndex] === 'Exclusion Analysis' &&
        getUnMatchingPlanExclusions();
    }, [
      unMatExclPageNumber,
      unMatExclSortField,
      unMatExclSortOrder,
      selectedTabIndex,
    ]);

    useEffect(() => {
      setDocuments(planDetails?.documents);
      setSelectedDoc(planDetails?.documents?.[0]);
    }, [planDetails]);

    // Load chat history when document changes
    useEffect(() => {
      if (selectedDoc?.original_url) {
        loadChatHistory();
      }
    }, [selectedDoc?.original_url]);

    useEffect(() => {
      if (
        (tabs[selectedTabIndex] === 'Red Flag Checks' ||
          tabs[selectedTabIndex] === 'Missing Checks') &&
        planDetails?.documents
      ) {
        if (provisions?.length === 0) {
          getMatrixData();
        }
        if (provisionsBySections?.length === 0) {
          getMatrixDataBySection();
        }
      }
    }, [planDetails, selectedTabIndex]);

    // Chat functions
    const loadChatHistory = async () => {
      if (!selectedDoc?.original_url || !selectedDoc?._id) {
        return;
      }

      // First initialize the chat with file_id and file_type
      try {
        await initialiseChatWithFile(selectedDoc._id, 'plan_doc');
      } catch (error: any) {
        console.error('Error initializing chat:', error);
        // Continue even if initialization fails
      }

      // Then load chat history
      // setChatHistoryLoading(true);
      try {
        // Use original_url (actual document URL) not document_url (blob URL)
        const response = await getChatHistory(selectedDoc.original_url);
        setChatMessages(response.data || []);
      } catch (error: any) {
        console.error('Error loading chat history:', error);
        // Silently fail - just start with empty chat history
        setChatMessages([]);
      } finally {
        // setChatHistoryLoading(false);
      }
    };

    const handleSendMessage = async (message?: string) => {
      const userMessage = message?.trim();

      if (!userMessage || !selectedDoc?.original_url) {
        return;
      }

      // Create temporary user message to display immediately
      const tempUserMessage: ChatMessage = {
        _id: `temp-${Date.now()}`,
        file_url: selectedDoc.original_url,
        file_id: selectedDoc?._id,
        message: userMessage,
        response: '',
        message_type: 'user',
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      // Add user message to chat immediately
      setChatMessages((prev) => [...prev, tempUserMessage]);
      setIsThinking(true);
      setChatLoading(true);

      try {
        // Call API with original_url (actual document URL), not document_url (blob URL)
        const response = await createChatMessage(
          selectedDoc.original_url,
          userMessage,
          selectedDoc?._id,
        );

        // Replace temporary message with actual response from API
        setChatMessages((prev) => {
          // Remove the temporary message
          const filtered = prev.filter(
            (msg) => msg._id !== tempUserMessage._id,
          );
          // Add the real message from API
          return [...filtered, response.data];
        });
        setIsThinking(false);
      } catch (error: any) {
        console.error('Error sending message:', error);

        // Instead of showing error, show "processing your message..." response
        const fallbackMessage: ChatMessage = {
          _id: `fallback-${Date.now()}`,
          file_url: selectedDoc.original_url,
          file_id: selectedDoc?._id,
          message: userMessage,
          response:
            'I encountered an error processing your message. Please try again.',
          message_type: 'user',
          status: 'failed',
          createdAt: new Date().toISOString(),
        };

        // Replace temporary message with fallback
        setChatMessages((prev) => {
          const filtered = prev.filter(
            (msg) => msg._id !== tempUserMessage._id,
          );
          return [...filtered, fallbackMessage];
        });
        setIsThinking(false);
      } finally {
        setChatLoading(false);
      }
    };

    //components
    const ChatSection = () => {
      // Format messages for the Chat component
      const formattedMessages: any[] = [];

      chatMessages.forEach((msg) => {
        // Add user message
        if (msg.message) {
          formattedMessages.push({
            id: `${msg._id}-user`,
            text: msg.message,
            isUser: true,
            timestamp: msg.createdAt,
          });
        }
        // Add assistant response with Markdown component
        if (msg.response) {
          formattedMessages.push({
            id: `${msg._id}-assistant`,
            text: msg.response,
            isUser: false,
            timestamp: msg.createdAt,
            component: <MarkdownMessage content={msg.response} />,
          });
        }
      });

      // Add thinking message if loading
      if (isThinking) {
        formattedMessages.push({
          id: 'thinking',
          text: 'Thinking...',
          isUser: false,
          timestamp: new Date().toISOString(),
        });
      }

      return (
        <Grid className={classes.chatSection}>
          <Chat
            height="425px"
            messages={formattedMessages}
            onSendMessage={handleSendMessage}
            isThinking={isThinking}
            isGenerating={chatLoading}
            suggestions={[]}
          />
        </Grid>
      );
    };

    const ViewDocument = () => {
      return (
        <Grid className={classes.documentViewContainer}>
          {docLoading ? (
            loader
          ) : (
            <>
              <Grid className={classes.documentSection}>
                {documents?.length > 1 && (
                  <Grid
                    className={classes.flex}
                    style={{ marginBottom: '16px' }}
                  >
                    <Typography
                      fontSize="16px"
                      lineHeight="22.4px"
                      fontType="medium"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                      margin="0px 8px 0px 0px"
                    >
                      {'Documents:'}
                    </Typography>
                    <DashedStandardDropdown
                      options={documents?.map((item: any) => item?._id)}
                      displayValue={documents?.map((item: any) => {
                        return `${item?.document_name.slice(0, 50)}... (${
                          item.document_type
                        })`;
                      })}
                      handleChange={(event) => {
                        const docName = event?.target?.value;
                        const selectedDoc = documents?.find(
                          (item: any) => docName === item?._id,
                        );
                        setSelectedDoc(selectedDoc);
                      }}
                      value={selectedDoc?._id}
                    />
                  </Grid>
                )}

                {selectedDoc?.document_url ? (
                  <Worker workerUrl={PDF_WORKER_URL}>
                    <Grid container style={{ marginBottom: '29px' }}>
                      <Typography
                        fontSize="16px"
                        lineHeight="22.4px"
                        fontType="medium"
                        textColor={
                          patientAccessTheme.palette.text.mediumemphasis
                        }
                        margin="0px 8px 0px 0px"
                      >
                        {'Document: '}
                      </Typography>
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontType="semiBold"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                        margin="0px 0px 20px 0px"
                      >
                        {selectedDoc?.document_name}
                      </Typography>
                      <Viewer
                        fileUrl={selectedDoc?.document_url}
                        renderPage={(props) => (
                          <>
                            {props.canvasLayer.children}
                            {props.textLayer.children}
                            {/* remove annotationLayer */}
                          </>
                        )}
                      />
                    </Grid>
                  </Worker>
                ) : (
                  <NoDataVisual content="NO FILE FOUND" />
                )}
              </Grid>
              <ChatSection />
            </>
          )}
        </Grid>
      );
    };

    const Reports = () => {
      return (
        <>
          {reportsLoading ? (
            loader
          ) : (
            <Grid>
              {messages.SPD_REPORT_CATEGORIES?.map(
                (category: any, categoryIndex: number) => {
                  const hasMatchingReports = reportData?.some((item: any) =>
                    category?.reports?.includes(item?.report_name),
                  );
                  return (
                    <div key={categoryIndex}>
                      {hasMatchingReports && (
                        <Typography
                          fontSize="14px"
                          lineHeight="24px"
                          fontType="semiBold"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          margin="0px 0px 16px 0px"
                        >
                          {category?.category}
                        </Typography>
                      )}
                      <Grid className={classes.reports}>
                        {reportData.length > 0 &&
                          reportData?.map((item: any, index: number) => {
                            return (
                              <>
                                {category?.reports?.includes(
                                  item?.report_name,
                                ) && (
                                  <div
                                    key={index}
                                    className={classes.eachReport}
                                  >
                                    <WasteCalculatorCard
                                      height="328px"
                                      width="358px"
                                      title={item?.report_name}
                                      subTitle={item?.description}
                                      image={ContractReport}
                                      titleFont="16px"
                                      showExternalLink={false}
                                      showTooltip={true}
                                      handleOnClick={() =>
                                        reportHandleOnClick(
                                          item?.report_name,
                                          item?.plan_doc_id,
                                          item?.compare_agreement_id,
                                          item?.status,
                                        )
                                      }
                                      truncatedSubTitleLength={100}
                                      showReportButton={true}
                                      reportButtonText={getReportStatusText(
                                        item?.status,
                                      )}
                                      reportStatus={item?.status}
                                      imageHeight="164px"
                                      showRegenerateButton={
                                        loginPageStore.userResponse.role
                                          .name ===
                                        UserAccessConstants.SUPER_ADMIN
                                      }
                                      regenerateTitle="Regenerate"
                                      handleRegenerate={() => {
                                        handleRegenerateReport(
                                          item?.report_name,
                                          item?.plan_doc_id,
                                          item?.compare_agreement_id,
                                        );
                                      }}
                                    />
                                  </div>
                                )}
                              </>
                            );
                          })}
                        {reportData.length === 0 && (
                          <NoDataVisual content="NO REPORTS FOUND" />
                        )}
                      </Grid>
                    </div>
                  );
                },
              )}
            </Grid>
          )}
        </>
      );
    };

    const PlanExclusionsAnalysis = () => {
      return (
        <>
          {matExclLoading || unMatExclLoading ? (
            loader
          ) : (
            <Grid>
              {loginPageStore.userResponse.role.name ===
                UserAccessConstants.SUPER_ADMIN && (
                <Grid style={{ marginBottom: '10px' }}>
                  <Button
                    label={'Regenerate'}
                    variant="contained"
                    height="37px"
                    width="auto"
                    onClick={() => handleRegenerate('Exclusion Analysis')}
                  />
                </Grid>
              )}
              {matchingExclusionsData?.totalCount === 0 ? (
                <NoDataVisual height="400px" content="No claims data found" />
              ) : (
                <>
                  <CardComponent
                    title={'Excluded Services from Plan (Claims Identified)'}
                    subTitle={`${
                      matchingExclusionsData?.totalCount
                    } Procedure Codes Identified with ${commasForNumbers(
                      matchingExclusionsData?.totalClaims?.toString()!,
                      0,
                    )} Claims with Total Plan Paid of $${commasForNumbers(
                      matchingExclusionsData?.totalPlanPaid?.toString()!,
                      2,
                    )}`}
                    width="1182px"
                    body={
                      matExclLoading ? (
                        loader
                      ) : (
                        <BasicTable
                          columns={matchingExclusionColumns}
                          rowsData={matchingExclusionsData?.exclusions}
                          displayType="row"
                          isCollapsiblePatientTable={true}
                          isToolTipFirstColumn={true}
                          showPagination={true}
                          pageCount={matExclTotalPages}
                          handlePagination={handleMatExclPageChange}
                          pageNumber={matExclPageNumber}
                          handleMenuItem={handleMatchingSort}
                          showOnHoverHeader={true}
                          columnId={columnId}
                          handleHeaderMouseEnter={(column: any) =>
                            handleHeaderMouseEnter(column, setColumnId)
                          }
                          handleHeaderMouseLeave={() =>
                            handleHeaderMouseLeave(setColumnId)
                          }
                        />
                      )
                    }
                  />
                  <CardComponent
                    title={
                      'Excluded Services from Plan (Claims Not Identified)'
                    }
                    width="1182px"
                    titleSubTitleMargin="10px"
                    mainGridStyles={{ marginTop: '32px' }}
                    body={
                      unMatExclLoading ? (
                        loader
                      ) : (
                        <BasicTable
                          columns={unMatchingExclusionColumns}
                          rowsData={unMatchingExclusionsData}
                          displayType="row"
                          showPagination={true}
                          pageCount={unMatExclTotalPages}
                          handlePagination={handleUnMatExclPageChange}
                          pageNumber={unMatExclPageNumber}
                          handleMenuItem={handleUnMatchingSort}
                          showOnHoverHeader={true}
                          columnId={columnId}
                          handleHeaderMouseEnter={(column: any) =>
                            handleHeaderMouseEnter(column, setColumnId)
                          }
                          handleHeaderMouseLeave={() =>
                            handleHeaderMouseLeave(setColumnId)
                          }
                        />
                      )
                    }
                  />
                </>
              )}
            </Grid>
          )}
        </>
      );
    };

    return (
      <>
        {pageLoading ? (
          loader
        ) : (
          <div>
            <Grid className={classes.breadcrumbs}>
              <Breadcrumbs itemList={breadcrumbsItemsList} />
            </Grid>
            <Grid className={classes.root}>
              <Grid>
                <PageTitleAndSubTitle
                  title={arrayToString(planNames)}
                  subTitle={`Access your ${
                    docType === 'SPD'
                      ? 'Summary Plan Description analysis'
                      : 'Summary of Benefits document'
                  } here.`}
                  showDownloadButton={false}
                  showQueryDataButton={false}
                  customButton={true}
                  customButtonText={customButtonText}
                  customButtonVariant={customButtonVariant}
                  customButtonClick={customButtonClick}
                  customButtonWidth={customButtonWidth}
                />
                <TabSelection
                  selectedTabIndex={selectedTabIndex}
                  setSelectedTabIndex={handleSelectedIndex}
                  tabList={tabs}
                />
              </Grid>
              <Grid
                className={classes.tabs}
                style={{ paddingRight: openDocument ? '20px' : '75px' }}
              >
                <Grid
                  className={classes.tabContent}
                  style={{ width: openDocument ? '55%' : '100%' }}
                >
                  {getTabsContent()}
                  <div id="scroll-top-element" />
                </Grid>
                {openDocument && (
                  <Grid className={classes.rightView}>
                    <PdfHighlighter
                      key={`${documentDetails?.documentUrl}-${documentDetails?.pageNumber}-${documentDetails?.chunks?.length}`}
                      pdfUrl={documentDetails?.documentUrl}
                      chunks={documentDetails?.chunks}
                      highlightIds={documentDetails?.chunks.map(
                        (item: any) => item.chunk_id,
                      )}
                      pageNumber={documentDetails?.pageNumber}
                      contentStyles={{ height: '480px' }}
                      onClose={() => setOpenDocument(false)}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {pageScrollDown('scroll-top-element')}
          </div>
        )}
        <Snackbar
          {...snackbarState}
          handleClose={handleCloseSnackBar}
          vertical="bottom"
          horizontal="right"
        />
      </>
    );
  },
);

export default PlanDocumentDetailsPage;
